import {ApiResponse, LoginRequest, LoginResponse, Application, StatisticDataResponse, HomeWidget, HomeWidgetLayoutItem, PinnableScreen, TermsState} from '../types';
import { storage } from '../utils/localStorage';
import { AppearanceSchema } from '../utils/appearanceSchema';
import { getTenantIdFromToken } from '../utils/jwt';
import { apiErrors } from './apiErrors';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'API_URL_NOT_SET';

export interface ApiTrafficEvent {
    method: string;
    url: string;
    requestBody?: any;
    status: number;
    responseBody?: any;
}

class ApiService {
    private isRefreshing = false;
    private refreshPromise: Promise<boolean> | null = null;
    private invalidSessionCallback: (() => void) | null = null;
    private requestObserver: ((event: ApiTrafficEvent) => void) | null = null;

    public setInvalidSessionCallback(callback: () => void) {
        this.invalidSessionCallback = callback;
    }

    /** Test recorder hook — observes every API request/response pair. */
    public setRequestObserver(callback: ((event: ApiTrafficEvent) => void) | null) {
        this.requestObserver = callback;
    }

    private notifyObserver(method: string, url: string, body: BodyInit | null | undefined, status: number, responseBody: any) {
        if (!this.requestObserver) return;
        try {
            let requestBody: any;
            if (typeof body === 'string') {
                try { requestBody = JSON.parse(body); } catch { requestBody = body; }
            }
            this.requestObserver({ method, url, requestBody, status, responseBody });
        } catch {
            // recorder must never break the app
        }
    }

    private getErrorMessageForStatus(status: number): string {
        switch (status) {
            case 400:
                return 'Neplatná požiadavka';
            case 403:
                return 'Prístup zamietnutý';
            case 404:
                return 'Záznam nebol nájdený';
            case 408:
                return 'Časový limit požiadavky vypršal';
            case 413:
                return 'Požiadavka je príliš veľká. Skúste zmenšiť objem odosielaných dát.';
            case 429:
                return 'Príliš veľa požiadaviek. Skúste to znova neskôr.';
            case 500:
                return 'Chyba servera';
            case 502:
                return 'Server je dočasne nedostupný';
            case 503:
                return 'Služba nie je dostupná. Skúste to znova neskôr.';
            case 504:
                return 'Časový limit servera vypršal';
            default:
                return `Chyba servera (${status})`;
        }
    }

    /**
     * An admin working as one of a tenant's users keeps their own token aside;
     * choosing a tenant or a user is always asked for with that one.
     */
    private getAuthHeaders(endpoint?: string): HeadersInit {
        const token = (endpoint?.startsWith('/tenants') && storage.getItem('adminToken')) || storage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'User-Agent': 'flowy-power-app',
            ...(token ? {'Authorization': `Bearer ${token}`} : {}),
        };
    }

    private async attemptReLogin(): Promise<boolean> {
        if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise;
        }

        this.isRefreshing = true;
        this.refreshPromise = (async () => {
            try {
                const savedEmail = storage.getItem('savedEmail');
                const savedPassword = storage.getItem('savedPassword');

                if (!savedEmail || !savedPassword) {
                    return false;
                }

                const response = await this.login({email: savedEmail, password: savedPassword});

                if (response.success) {
                    // Získaj nový Mercure token
                    try {
                        const mercureResponse = await this.getMercureToken();
                        if (mercureResponse.success && mercureResponse.data.token) {
                            storage.setItem('mercureToken', mercureResponse.data.token);
                        }
                    } catch (error) {
                        console.error('[ApiService] Failed to get Mercure token on re-login:', error);
                    }
                    return true;
                } else {
                    return false;
                }
            } catch (error) {
                return false;
            } finally {
                this.isRefreshing = false;
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        isRetry: boolean = false
    ): Promise<ApiResponse<T>> {
        const url = `${API_BASE_URL}${endpoint}`;

        const config: RequestInit = {
            ...options,
            headers: {
                ...this.getAuthHeaders(endpoint),
                ...options.headers,
            },
        };

        try {
            const response = await fetch(url, config);
            // Report the HTTP status even when an upstream proxy returns HTML instead of JSON.
            apiErrors.report(response.status, config.method || 'GET');

            let data: any;
            try {
                data = await response.json();
            } catch (parseError) {
                // JSON parsing failed - handle based on status code
                if (!response.ok) {
                    return {
                        success: false,
                        data: {} as T,
                        message: this.getErrorMessageForStatus(response.status),
                    };
                }
                // If response was OK but JSON parsing failed
                return {
                    success: false,
                    data: {} as T,
                    message: 'Neplatný formát odpovede zo servera',
                };
            }

            if (response.status !== 401 || isRetry) {
                this.notifyObserver((config.method as string) || 'GET', endpoint, options.body, response.status, data);
            }

            if (response.status === 401 && !isRetry) {
                const savedEmail = storage.getItem('savedEmail');
                const savedPassword = storage.getItem('savedPassword');

                if (!savedEmail || !savedPassword) {
                    storage.removeItem('token');
                    storage.removeItem('user');

                    if (this.invalidSessionCallback) {
                        this.invalidSessionCallback();
                    }

                    return {
                        success: false,
                        data: data,
                        message: 'Session expired, please login again',
                    };
                }

                const reLoginSuccess = await this.attemptReLogin();

                if (reLoginSuccess) {
                    return this.request<T>(endpoint, options, true);
                } else {
                    storage.removeItem('savedEmail');
                    storage.removeItem('savedPassword');
                    storage.removeItem('token');
                    storage.removeItem('user');

                    if (this.invalidSessionCallback) {
                        this.invalidSessionCallback();
                    }
                    return {
                        success: false,
                        data: data,
                        message: 'Invalid credentials, please login again',
                    };
                }
            }

            if (!response.ok) {
                return {
                    success: false,
                    data: data,
                    message: data.message || 'An error occurred',
                    errors: data.errors,
                };
            }

            if (data.success === false) {
                return {
                    success: false,
                    data: data,
                    message: data.message,
                    errors: data.errors,
                };
            }

            return {
                success: true,
                data: data,
            };
        } catch (error) {
            return {
                success: false,
                data: {} as T,
                message: 'Network error occurred',
            };
        }
    }

    async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {method: 'GET', ...options});
    }

    async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {method: 'DELETE'});
    }

    async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
        return this.postLogin(`${API_BASE_URL}/login`, credentials);
    }

    /**
     * "Sign in with Google": the Google ID token the GIS button hands over is
     * exchanged for the same JWT a password login gets.
     */
    async loginWithGoogle(credential: string): Promise<ApiResponse<LoginResponse>> {
        return this.postLogin(`${API_BASE_URL}/login/google`, {credential});
    }

    private async postLogin(url: string, payload: object): Promise<ApiResponse<LoginResponse>> {

        const config: RequestInit = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'User-Agent': 'flowy-power-app',
                'Cookie': 'main_deauth_profile_token=1c5b8b',
            },
            body: JSON.stringify(payload),
        };

        try {
            const response = await fetch(url, config);
            apiErrors.report(response.status, config.method || 'POST');
            const data = await response.json();

            if (response.ok) {
                const tokenData = data.data || data;
                if (tokenData.token) {
                    storage.setItem('token', tokenData.token);
                    await this.restoreChosenTenant(tokenData.token);
                }
                if (tokenData.user) {
                    storage.setItem('user', JSON.stringify(tokenData.user));
                }

                return {
                    success: true,
                    data: data,
                };
            } else {
                return {
                    success: false,
                    data: data,
                    message: data.message || 'Login failed',
                };
            }
        } catch (error) {
            return {
                success: false,
                data: {} as LoginResponse,
                message: 'Network error occurred',
            };
        }
    }

    async getApplications(): Promise<ApiResponse<Application[]>> {
        return this.get<Application[]>('/applications/');
    }

    /**
     * Changes whenever the server would build /applications anew for this
     * user; null when it cannot tell. A background check, so it has its own
     * fetch: a failure must not raise the error notice or the re-login flow.
     */
    async getApplicationsRevision(): Promise<string | null> {
        try {
            const response = await fetch(`${API_BASE_URL}/applications/revision`, {headers: this.getAuthHeaders()});
            if (!response.ok) return null;
            const body = await response.json();
            return typeof body?.revision === 'string' ? body.revision : null;
        } catch {
            return null;
        }
    }

    /** The admin-defined appearance keys, values and colours; see utils/appearanceSchema. */
    async getAppearanceSchema(): Promise<ApiResponse<AppearanceSchema>> {
        return this.get<AppearanceSchema>('/appearance/schema');
    }

    /**
     * Screen rows go over QUERY: a GET with a body. The same parameters as
     * before, as a JSON object, so a filter tree of any size fits where the
     * query string would not.
     */
    async getScreenData(
        screenId: string,
        filters?: Record<string, any>,
        search?: string,
        filterGroups?: any,
        userFilterId?: string | null,
        afterId?: number | null,
        pageSize?: number,
        signal?: AbortSignal,
        sort?: Record<string, 'ASC' | 'DESC'>
    ): Promise<ApiResponse<any>> {
        const body: Record<string, any> = {};

        if (filters && Object.keys(filters).length > 0) {
            // null/undefined → empty string; the key still needs to be
            // present so the backend recognizes the filter as set.
            body.filters = Object.fromEntries(
                Object.entries(filters).map(([key, value]) => [key, value == null ? '' : String(value)])
            );
        }

        if (userFilterId) {
            body.userFilter = userFilterId;
        } else if (filterGroups) {
            body.filter_groups = filterGroups;
        }

        if (search) {
            body.search = search;
        }

        if (afterId !== undefined && afterId !== null) {
            body.afterId = afterId;
        }

        if (pageSize !== undefined) {
            body.pageSize = pageSize;
        }

        if (sort && Object.keys(sort).length > 0) {
            body.sort = sort;
        }

        return this.request<any>(`/screens/${screenId}/data`, {
            method: 'QUERY',
            body: JSON.stringify(body),
            ...(signal ? {signal} : {}),
        });
    }

    async createFormData(formId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.post<any>(`/form/${formId}/create`, data);
    }

    async createDynamicFormData(formId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.post<any>(`/dynamic-form/${formId}/create`, data);
    }

    /**
     * One record of a screen: the screen's list asked for that id, so it is the row the list
     * shows for it. Answers in the shape of a single-record response.
     *
     * @param filters the screen's request parameters, the same ones its list is loaded with
     * @param missingIsNotFound a record the list does not return raises the 404 page, as a page
     *        opened for that record needs; false where it only fills in part of another page
     */
    async getScreenRecord(
        screenId: string,
        recordId: string | number,
        filters?: Record<string, any>,
        missingIsNotFound: boolean = true
    ): Promise<ApiResponse<any>> {
        const body: Record<string, any> = {ids: [recordId], pageSize: 1, disablePager: true};
        if (filters && Object.keys(filters).length > 0) {
            body.filters = Object.fromEntries(
                Object.entries(filters).map(([key, value]) => [key, value == null ? '' : String(value)])
            );
        }

        const response = await this.request<any>(`/screens/${screenId}/data`, {method: 'QUERY', body: JSON.stringify(body)});
        if (!response.success) {
            return response;
        }

        const record = response.data?.data?.[0];
        if (record === undefined) {
            if (missingIsNotFound) apiErrors.report(404, 'QUERY');
            return {success: false, data: {}, message: this.getErrorMessageForStatus(404)};
        }

        return {success: true, data: {success: true, data: record}};
    }

    // Record read shaped by the DynamicForm's fields — independent of screens,
    // so a form column doesn't need a (hidden) screen column to get its value.
    async getDynamicFormRecord(formId: string, recordId: string | number): Promise<ApiResponse<any>> {
        return this.get<any>(`/dynamic-form/${formId}/data/${recordId}`);
    }

    async deleteScreenData(screenId: string, dataId: string | number): Promise<ApiResponse<any>> {
        return this.delete<any>(`/screens/${screenId}/data/${dataId}`);
    }

    async updateColumnValue(screenId: string, dataId: string | number, columnId: string, value: any): Promise<ApiResponse<any>> {
        return this.post<any>(`/screens/${screenId}/data/${dataId}/update/${columnId}`, {value});
    }

    async updateFormData(formId: string, dataId: string | number, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.patch<any>(`/form/${formId}/update/${dataId}`, data);
    }

    async updateDynamicFormData(formId: string, dataId: string | number, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.patch<any>(`/dynamic-form/${formId}/update/${dataId}`, data);
    }

    async getUserFilters(screenId: string): Promise<ApiResponse<any[]>> {
        return this.get<any[]>(`/user-filters/screen/${screenId}`);
    }

    async createUserFilter(screenId: string, name: string, conditions: any): Promise<ApiResponse<any>> {
        return this.post<any>('/user-filters', {
            screenId,
            name,
            conditions
        });
    }

    async updateUserFilter(filterId: string, name: string, conditions: any): Promise<ApiResponse<any>> {
        return this.patch<any>(`/user-filters/${filterId}`, {
            name,
            conditions
        });
    }

    async deleteUserFilter(filterId: string): Promise<ApiResponse<any>> {
        return this.delete<any>(`/user-filters/${filterId}`);
    }

    async setUserFilterDefault(filterId: string, isDefault: boolean): Promise<ApiResponse<any>> {
        return this.patch<any>(`/user-filters/${filterId}`, { isDefault });
    }

    async getFilterOperators(): Promise<ApiResponse<any>> {
        return this.get<any>('/filter-operators');
    }

    // --- Statistic builder (client admin only, SQL never reaches the client) ---

    async getUserGroups(): Promise<ApiResponse<any>> {
        return this.get<any>('/user-groups');
    }

    async createStatistic(message: string, draft?: Record<string, any>): Promise<ApiResponse<any>> {
        return this.post<any>('/statistics', {message, ...(draft ?? {})});
    }

    async chatStatistic(statisticId: string, message: string): Promise<ApiResponse<any>> {
        return this.post<any>(`/statistics/${statisticId}/chat`, {message});
    }

    async getStatisticDefinition(statisticId: string): Promise<ApiResponse<any>> {
        return this.get<any>(`/statistics/${statisticId}/definition`);
    }

    async saveStatistic(statisticId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.post<any>(`/statistics/${statisticId}/save`, data);
    }

    // Widgets of a statistic (client admin); every call answers with the whole definition
    async createStatisticWidget(statisticId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.post<any>(`/statistics/${statisticId}/widgets`, data);
    }

    async updateStatisticWidget(statisticId: string, widgetId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.patch<any>(`/statistics/${statisticId}/widgets/${widgetId}`, data);
    }

    async deleteStatisticWidget(statisticId: string, widgetId: string): Promise<ApiResponse<any>> {
        return this.delete<any>(`/statistics/${statisticId}/widgets/${widgetId}`);
    }

    // --- AI bots (client admin) ---
    async getAiBots(): Promise<ApiResponse<any>> {
        return this.get<any>('/ai-bots');
    }

    async getAiBotSystemUsers(): Promise<ApiResponse<any>> {
        return this.get<any>('/ai-bots/system-users');
    }

    async createAiBot(data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.post<any>('/ai-bots', data);
    }

    async updateAiBot(botId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
        return this.patch<any>(`/ai-bots/${botId}`, data);
    }

    async deleteAiBot(botId: string): Promise<ApiResponse<any>> {
        return this.delete<any>(`/ai-bots/${botId}`);
    }

    async deleteStatistic(statisticId: string): Promise<ApiResponse<any>> {
        return this.delete<any>(`/statistics/${statisticId}`);
    }

    // --- Home page widgets (statistic widgets and screens the user pinned to their own home page) ---

    async getHomeWidgets(): Promise<ApiResponse<HomeWidget[]>> {
        return this.unwrapData<HomeWidget[]>(await this.get<any>('/home-widgets'));
    }

    async addHomeWidget(source: {widgetId: string} | {screenId: string}): Promise<ApiResponse<HomeWidget[]>> {
        return this.unwrapData<HomeWidget[]>(await this.post<any>('/home-widgets', source));
    }

    /** By the pin's id. */
    async removeHomeWidget(id: string): Promise<ApiResponse<HomeWidget[]>> {
        return this.unwrapData<HomeWidget[]>(await this.delete<any>(`/home-widgets/${id}`));
    }

    /** Screens of the applications the user sees, offered by „Pridať widget“. */
    async getPinnableScreens(): Promise<ApiResponse<PinnableScreen[]>> {
        return this.unwrapData<PinnableScreen[]>(await this.get<any>('/home-widgets/screens'));
    }

    /** Positions and sizes of the whole grid after a drag or resize. */
    async saveHomeWidgetLayout(items: HomeWidgetLayoutItem[]): Promise<ApiResponse<HomeWidget[]>> {
        return this.unwrapData<HomeWidget[]>(await this.put<any>('/home-widgets/layout', {items}));
    }

    /** request() wraps the raw body as {success, data: body}; these endpoints answer {success, data: [...]}. */
    private unwrapData<T>(response: ApiResponse<any>): ApiResponse<T> {
        const body = response.data;
        return {...response, data: (body && typeof body === 'object' && 'data' in body ? body.data : body) as T};
    }

    async getStatisticData(
        statisticId: string,
        filters?: Record<string, any>,
        signal?: AbortSignal,
    ): Promise<StatisticDataResponse> {
        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    params.append(`filters[${key}]`, String(value));
                }
            });
        }
        const query = params.toString();
        // request() wraps the raw body as {success, data: body} — unwrap it
        const response = await this.get<any>(`/statistics/${statisticId}/data${query ? '?' + query : ''}`, {signal});
        const body = response.data as StatisticDataResponse | undefined;
        if (!response.success || !body || body.success === false) {
            return {
                success: false,
                error: body?.error || response.message || 'Štatistiku sa nepodarilo načítať.',
                columns: [],
                rows: [],
                rowCount: 0,
            };
        }
        return body;
    }

    async saveTestScenario(payload: { displayName: string; description?: string; startPath: string; steps: any[] }): Promise<ApiResponse<any>> {
        return this.post<any>('/test-scenarios', payload);
    }

    async executeWorkflow(url: string, data: any): Promise<ApiResponse<any>> {
        const apiUrl = `${API_BASE_URL}${url}`;

        const config: RequestInit = {
            method: 'POST',
            headers: {
                ...this.getAuthHeaders(),
            },
            body: JSON.stringify(data),
        };

        try {
            const response = await fetch(apiUrl, config);
            apiErrors.report(response.status, config.method || 'POST');
            const contentType = response.headers.get('content-type') || '';

            // Handle 401 separately
            if (response.status === 401) {
                const savedEmail = storage.getItem('savedEmail');
                const savedPassword = storage.getItem('savedPassword');

                if (!savedEmail || !savedPassword) {
                    storage.removeItem('token');
                    storage.removeItem('user');
                    if (this.invalidSessionCallback) {
                        this.invalidSessionCallback();
                    }
                    return {
                        success: false,
                        data: {},
                        message: 'Session expired, please login again',
                    };
                }

                const reLoginSuccess = await this.attemptReLogin();
                if (reLoginSuccess) {
                    return this.executeWorkflow(url, data);
                } else {
                    storage.removeItem('savedEmail');
                    storage.removeItem('savedPassword');
                    storage.removeItem('token');
                    storage.removeItem('user');
                    if (this.invalidSessionCallback) {
                        this.invalidSessionCallback();
                    }
                    return {
                        success: false,
                        data: {},
                        message: 'Invalid credentials, please login again',
                    };
                }
            }

            if (!response.ok) {
                // Try to parse error as JSON
                try {
                    const errorData = await response.json();
                    return {
                        success: false,
                        data: errorData,
                        message: errorData.message || 'An error occurred',
                        errors: errorData.errors,
                    };
                } catch (e) {
                    const errorText = await response.text();
                    return {
                        success: false,
                        data: {},
                        message: errorText || 'An error occurred',
                    };
                }
            }

            // Check if response is a binary file (PDF, image, etc.)
            if (!contentType.includes('application/json')) {
                // Get filename from Content-Disposition header
                const contentDisposition = response.headers.get('content-disposition') || '';
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                let filename = filenameMatch && filenameMatch[1] ? filenameMatch[1].replace(/['"]/g, '') : '';

                // Fallback filename based on content type
                if (!filename) {
                    const extension = contentType.split('/')[1]?.split(';')[0] || 'file';
                    filename = `download.${extension}`;
                }

                // Read response as blob and convert to base64
                const blob = await response.blob();
                const base64Data = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const result = reader.result as string;
                        // Remove data URL prefix to get just the base64 string
                        const base64 = result.split(',')[1];
                        resolve(base64);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });

                // Return as FileData structure
                return {
                    success: true,
                    data: {
                        file: {
                            mimeType: contentType.split(';')[0],
                            data: base64Data,
                            filename: filename
                        }
                    }
                };
            }

            // Try to parse as JSON
            let responseData: any;
            try {
                responseData = await response.json();
            } catch (e) {
                // If JSON parsing fails, try as text
                const text = await response.text();

                // Check if it's a data URL (e.g., data:application/pdf;base64,...)
                if (text.startsWith('data:')) {
                    const match = text.match(/^data:([^;]+);base64,(.+)$/);
                    if (match) {
                        const [, mimeType, base64Data] = match;
                        // Return as FileData structure
                        responseData = {
                            data: {
                                file: {
                                    mimeType: mimeType,
                                    data: base64Data,
                                    filename: `download.${mimeType.split('/')[1] || 'file'}`
                                }
                            }
                        };
                    } else {
                        // Invalid data URL format
                        return {
                            success: false,
                            data: {},
                            message: 'Invalid file format received',
                        };
                    }
                } else {
                    // Not a data URL, treat as error
                    return {
                        success: false,
                        data: {},
                        message: text || 'Unexpected response format',
                    };
                }
            }

            this.notifyObserver('POST', url, config.body, response.status, responseData);

            if (responseData.success === false) {
                return {
                    success: false,
                    data: responseData,
                    message: responseData.message,
                    errors: responseData.errors,
                };
            }

            return {
                success: true,
                data: responseData,
            };
        } catch (error) {
            return {
                success: false,
                data: {},
                message: 'Network error occurred',
            };
        }
    }

    /** Tenants a full admin may work in (admin only). */
    async getTenants(): Promise<ApiResponse<any>> {
        return this.get<any>('/tenants');
    }

    /**
     * A fresh JWT for the chosen tenant (admin only); stored as the session
     * token, so everything after it runs in that tenant.
     */
    async selectTenant(tenantId: number, userId?: number): Promise<ApiResponse<any>> {
        const response = await this.post<any>(`/tenants/${tenantId}/select`, userId ? {userId} : {});
        const token = response.success ? response.data?.data?.token : null;
        if (token) {
            if (userId) {
                // The session becomes that user's; the admin's own token is what lets them choose again.
                if (!storage.getItem('adminToken')) {
                    storage.setItem('adminToken', storage.getItem('token') ?? '');
                }
                storage.setItem('chosenUserId', String(userId));
            } else {
                storage.removeItem('adminToken');
                storage.removeItem('chosenUserId');
            }
            storage.setItem('token', token);
            storage.setItem('chosenTenantId', String(tenantId));
        }
        return response;
    }

    /** The accounts of a tenant a full admin may work as (admin only). */
    async getTenantUsers(tenantId: number): Promise<ApiResponse<any>> {
        return this.get<any>(`/tenants/${tenantId}/users`);
    }

    /**
     * A login (the silent re-login with the remembered password included)
     * always yields the admin's own tenant; when they had chosen another one,
     * or one of its users to work as, take it again so the session stays
     * where they were working.
     */
    private async restoreChosenTenant(token: string): Promise<void> {
        const chosen = Number(storage.getItem('chosenTenantId'));
        const chosenUser = Number(storage.getItem('chosenUserId')) || undefined;
        // The token this login issued replaces the one kept aside.
        storage.removeItem('adminToken');
        if (!chosen || storage.getItem('tenantChosen') !== '1' || (!chosenUser && getTenantIdFromToken(token) === chosen)) {
            return;
        }
        try {
            await this.selectTenant(chosen, chosenUser);
        } catch (error) {
            console.error('[ApiService] Failed to restore the chosen tenant after login:', error);
        }
    }

    /** Whether the company accepted the current terms and conditions, and whether this user has to. */
    async getTerms(): Promise<ApiResponse<TermsState>> {
        return this.unwrapData<TermsState>(await this.get<any>('/terms'));
    }

    /** The company's consent to the version its client admin was shown. */
    async acceptTerms(version: string): Promise<ApiResponse<TermsState>> {
        return this.unwrapData<TermsState>(await this.post<any>('/terms/accept', {version}));
    }

    async getMercureToken(): Promise<ApiResponse<{ token: string }>> {
        return this.get<{ token: string }>('/mercure/token');
    }

    async forgotPassword(email: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
        const url = `${API_BASE_URL}/password/forgot`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            apiErrors.report(response.status, 'POST');

            const data = await response.json();
            return {
                success: data.success ?? response.ok,
                data,
                message: data.message,
            };
        } catch (error) {
            return {
                success: false,
                data: {} as any,
                message: 'Chyba siete. Skúste to znova neskôr.',
            };
        }
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
        return this.post<{ success: boolean; message: string }>('/password/change', {
            currentPassword,
            newPassword,
        });
    }

    async resetPassword(token: string, password: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
        const url = `${API_BASE_URL}/password/reset`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            apiErrors.report(response.status, 'POST');

            const data = await response.json();
            return {
                success: data.success ?? response.ok,
                data,
                message: data.message,
            };
        } catch (error) {
            return {
                success: false,
                data: {} as any,
                message: 'Chyba siete. Skúste to znova neskôr.',
            };
        }
    }
}

export const apiService = new ApiService();
